import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { bufferTime, catchError, debounceTime, filter, mergeMap, switchMap, throttleTime } from 'rxjs/operators';

import {
  DerivativesOpenedPositionProjection,
  DerivativesOrderSide,
  DerivativesOrderType,
  OrderAction,
} from 'common/api/autogenerated/trading-gateway';

import { DerivativeOrderFullyExecutedPopup } from 'common/popups';

import { notify } from 'common/hooks/useNotification';
import { CommonEpic } from 'common/types/redux';

import { history } from 'services/history';

import { actionShowModal } from 'common/redux/modal/actions';
import { isBuy } from '../orders/utils';
import derivativesActionTypes from './derivatives.actionTypes';
import * as actions from './derivatives.actions';
import * as derivativesApi from './derivatives.api';
import { mapOpenedPositionsFromServer } from './derivatives.mapper';

export const epicGetDerivatives: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVES),
    mergeMap(() =>
      from(api.getDerivatives()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetDerivatives(response), actions.actionGetDerivativesGroups())),
        catchError((error) => of(actions.actionErrorGetDerivatives(error))),
      ),
    ),
  );

export const epicGetDerivativeInfo: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVE_INFO, derivativesActionTypes.REFRESH_GET_DERIVATIVE_INFO),
    mergeMap((action) =>
      from(api.getDerivativeInfo(action.payload)).pipe(
        mergeMap((response) => of(actions.actionSuccessGetDerivativeInfo(response))),
        catchError((error) => of(actions.actionErrorGetDerivativeInfo(error))),
      ),
    ),
  );

export const epicGetDerivativesGroups: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVES_GROUPS),
    mergeMap(() =>
      from(api.getDerivativesGroups()).pipe(
        mergeMap((response) => {
          const { isAuthenticated } = _state$.value.common.user;
          if (isAuthenticated)
            return of(actions.actionSuccessGetDerivativesGroups(response), actions.actionGetFavoriteDerivatives());
          return of(actions.actionSuccessGetDerivativesGroups(response));
        }),
        catchError((error) => of(actions.actionErrorGetDerivativesGroups(error))),
      ),
    ),
  );

export const epicGetFavoriteDerivatives: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_FAVORITE_DERIVATIVES),
    mergeMap(() =>
      from(api.getFavoriteDerivatives()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetFavoriteDerivatives(response))),
        catchError((error) => of(actions.actionErrorGetFavoriteDerivatives(error))),
      ),
    ),
  );

export const epicSetFavoriteDerivative: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_SET_FAVORITE_DERIVATIVES),
    mergeMap((action) => {
      const { favorites } = _state$.value.derivativesReducer;
      const endpoint = () =>
        favorites.includes(action.payload)
          ? api.deleteFavoriteDerivatives(action.payload)
          : api.setFavoriteDerivatives(action.payload);
      return from(endpoint()).pipe(
        mergeMap((response) =>
          of(actions.actionSuccessSetFavoriteDerivatives(response), actions.actionGetFavoriteDerivatives()),
        ),
        catchError((error) => of(actions.actionErrorSetFavoriteDerivatives(error))),
      );
    }),
  );

export const epicGetDerivativesTrend: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVES_TREND),
    bufferTime(2000),
    filter((stream) => !!stream.length),
    switchMap((actionsCollection) => {
      const codes = actionsCollection.reduce((acc: string[], curr) => [...acc, ...curr.payload], []);
      return from(api.getDerivativesDailyCandles(codes)).pipe(
        mergeMap((response) => of(actions.actionSuccessGetDerivativesTrend(response.data || []))),
        catchError((error) => of(actions.actionErrorGetDerivativesTrend(error))),
      );
    }),
  );

export const epicGetDerivativesOrders: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(
      derivativesActionTypes.LOADING_GET_DERIVATIVES_ORDERS,
      derivativesActionTypes.SUCCESS_DELETE_DERIVATIVES_ORDERS,
    ),
    debounceTime(500),
    mergeMap(() =>
      from(api.getDerivativeOpenOrders()).pipe(
        mergeMap((response) =>
          of(actions.actionSuccessGetDerivativesOrders(response), actions.actionGetLatestDerivativeClosedOrder()),
        ),
        catchError((error) => of(actions.actionErrorGetDerivatives(error))),
      ),
    ),
  );

export const epicGetDerivativesClosedOrders: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVES_CLOSED_ORDERS),
    debounceTime(500),
    mergeMap(() =>
      from(api.getDerivativeClosedOrders()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetDerivativesClosedOrders(response))),
        catchError((error) => of(actions.actionErrorGetDerivativesClosedOrders(error))),
      ),
    ),
  );

export const epicGetLatestClosedOrder: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_LATEST_DERIVATIVES_CLOSED_ORDER),
    debounceTime(500),
    mergeMap(() =>
      from(api.getDerivativeClosedOrders(1)).pipe(
        mergeMap((response) => of(actions.actionSuccessLatestDerivativeClosedOrder(response[0] || null))),
        catchError((error) => of(actions.actionErrorLatestDerivativeClosedOrder(error))),
      ),
    ),
  );

export const epicUpdatePrice: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_PRICE),
    throttleTime(300),
    mergeMap((action) =>
      of(
        actions.actionReceiveDerivativePriceUpdateSuccess({
          ...action.payload,
        }),
      ),
    ),
  );

export const epicUpdateMarkPrice: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_MARK_PRICE),
    throttleTime(300),
    mergeMap((action) =>
      of(
        actions.actionReceiveDerivativeMarkPriceUpdateSuccess({
          ...action.payload,
        }),
      ),
    ),
  );

export const epicGetDerivativesPositions: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVES_POSITIONS),
    debounceTime(500),
    mergeMap(() =>
      from(api.getDerivativeOpenPositions()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetDerivativesPositions(response))),
        catchError((error) => of(actions.actionErrorGetDerivativesPositions(error))),
      ),
    ),
  );

export const epicGetDerivativesHistory: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVES_HISTORY),
    debounceTime(500),
    mergeMap(() =>
      from(api.getDerivativesHistory(undefined)).pipe(
        mergeMap((response) => of(actions.actionSuccessGetDerivativesHistory(response))),
        catchError((error) => of(actions.actionErrorGetDerivativesHistory(error))),
      ),
    ),
  );

export const epicReceiveOrderCreated: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_ORDER_OPENED),
    mergeMap((action) => {
      const { side, tradingPairCode, type } = action.payload;
      const [baseCurrency, quoteCurrency] = String(tradingPairCode).split('-');
      const isMarketOrder = type === DerivativesOrderType.MARKET;

      const notificationSide = isBuy(side) ? 'buy' : 'sell';

      if (!isMarketOrder) {
        notify({
          titleFn: (translate) =>
            translate(`mobile:notifications:derivative:order:${notificationSide}:created`).replace(
              '{symbol}',
              `${baseCurrency}/${quoteCurrency}`,
            ),
        });
      }

      return of(actions.actionReceiveDerivativeOrderOpenedSuccess(action.payload));
    }),
  );

export const epicReceivePositionCreated: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_POSITION_CREATED),
    mergeMap((action) => {
      const { side, tradingPairCode } = action.payload;
      const [baseCurrency, quoteCurrency] = String(tradingPairCode).split('-');
      notify({
        titleFn: (translate) =>
          translate(`mobile:notifications:derivative:position:${side}:created`).replace(
            '{symbol}',
            `${baseCurrency}/${quoteCurrency}`,
          ),
      });

      if (_state$.value.derivativesReducer.inUseTransactions.positions.length)
        return of(
          actions.actionReceiveDerivativePositionCreatedSuccess(action.payload),
          actions.actionLoadingGetDerivativesInUsePositions(action.payload?.settlementCurrency || ''),
        );

      return of(actions.actionReceiveDerivativePositionCreatedSuccess(action.payload));
    }),
  );

export const epicReceivePositionUpdated: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_POSITION_UPDATED),
    bufferTime(1000),
    filter((stream) => !!stream.length),
    switchMap((actionsCollection) => {
      const updates = actionsCollection.reduce(
        (positions: Record<number, DerivativesOpenedPositionProjection>, action) => {
          if (!action.payload.id) return positions;
          return { ...positions, [action.payload.id]: action.payload };
        },
        {},
      );

      return of(actions.actionReceiveDerivativePositionUpdatedSuccess(updates));
    }),
  );

export const epicReceivePositionClosed: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_POSITION_CLOSED),
    mergeMap((action) => {
      const { side, tradingPairCode, id = 0 } = action.payload;
      const [baseCurrency, quoteCurrency] = String(tradingPairCode).split('-');
      notify({
        titleFn: (translate) =>
          translate(`mobile:notifications:derivative:position:${side}:closed`).replace(
            '{symbol}',
            `${baseCurrency}/${quoteCurrency}`,
          ),
      });

      // If position is closed during position details view - close it by navigation back
      if (window.location.pathname.includes(String(id))) history.goBack();

      return of(actions.actionReceiveDerivativePositionClosedSuccess(action.payload));
    }),
  );

export const epicGetDerivativesInUseOrders: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVIES_IN_USE_ORDERS),
    debounceTime(500),
    mergeMap((action) =>
      from(api.getInOrdersOpenOrders(action.payload.settlementCurrency)).pipe(
        mergeMap((response) => of(actions.actionGetDerivativesInUseOrders(response))),
        catchError((error) => of(actions.actionErrorGetDerivativesInUseOrders(error))),
      ),
    ),
  );

export const epicGetDerivativesInUsePositions: CommonEpic<typeof derivativesApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(derivativesActionTypes.LOADING_GET_DERIVATIVIES_IN_USE_POSITIONS),
    debounceTime(500),
    mergeMap((action) =>
      from(api.getInOrdersOpenPositions(action.payload.settlementCurrency)).pipe(
        mergeMap((response) => of(actions.actionGetDerivativesInUsePositions(mapOpenedPositionsFromServer(response)))),
        catchError((error) => of(actions.actionErrorGetDerivativesInUsePositions(error))),
      ),
    ),
  );

export const epicReceiveOrderUpdatedOrClosed: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(
      derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_ORDER_UPDATED,
      derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_ORDER_CLOSED,
    ),
    filter(
      () =>
        _state$.value.derivativesReducer.inUseTransactions.orders.length > 0 ||
        _state$.value.derivativesReducer.inUseTransactions.positions.length > 0,
    ),
    mergeMap((action) => of(actions.actionLoadingGetDerivativesInUseOrders(action.payload?.settlementCurrency || ''))),
  );

export const epicReceivePositionCreatedOrClosed: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(
      derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_POSITION_CREATED,
      derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_POSITION_CLOSED,
    ),
    filter(
      () =>
        _state$.value.derivativesReducer.inUseTransactions.orders.length > 0 ||
        _state$.value.derivativesReducer.inUseTransactions.positions.length > 0,
    ),
    mergeMap((action) =>
      of(
        actions.actionLoadingGetDerivativesInUsePositions(
          action.payload?.settlementCurrency ||
            _state$.value.derivativesReducer.inUseTransactions.lastSettlementCurrency ||
            '',
        ),
      ),
    ),
  );

export const epicReceiveDerivativeOrderClosedShowSuccessPopup: CommonEpic<typeof derivativesApi> = (action$, _state$) =>
  action$.pipe(
    ofType(derivativesActionTypes.SIGNALR_INCOME_RECEIVE_DERIVATIVE_ORDER_CLOSED),
    mergeMap((action) => {
      const { id, tradingPairCode, quantity, side } = action.payload;
      const [baseCurrency, quoteCurrency] = String(tradingPairCode).split('-');

      if (_state$.value.derivativesReducer.orderIsEditing === id) {
        return of(
          actionShowModal(
            DerivativeOrderFullyExecutedPopup({
              baseCurrency: baseCurrency || '',
              quotedCurrency: quoteCurrency || '',
              action: side === DerivativesOrderSide.BUY ? OrderAction.BUY : OrderAction.SELL,
              amount: quantity || 0,
              type: 'derivative',
            }),
          ),
        );
      }

      return of();
    }),
  );
